/**
 * Support workspace (FOR-350, owner only): our products' support inbox.
 * Types and pure helpers only, no React, so tests/support.test.mjs can pin
 * them. Shapes follow SupportAdminController in the API repo
 * (docs/support-api.md there is the product contract).
 */

export type SupportFolder = "inbox" | "waiting" | "all" | "closed" | "spam" | "test"
export type SupportKind = "contact" | "support" | "feedback"
export type SupportStatus = "open" | "waiting_on_team" | "waiting_on_customer" | "resolved" | "closed"
export type SupportCategory = "bug" | "idea" | "question" | "other"
export type SupportMessageType = "customer" | "team" | "system" | "note"
/** SupportMailer: none (nothing to send), sent, failed, held (sandbox), test (a test conversation). */
export type SupportEmailStatus = "none" | "sent" | "failed" | "held" | "test"
export type SupportMode = "sandbox" | "live"
export type SupportBlockType = "email" | "domain" | "ip"
export type ContextTone = "default" | "success" | "warning" | "danger"

export interface SupportProjectSettings {
  uploads: boolean
  max_upload_mb: number
  feedback_categories: string[]
  alert_on_bug: boolean
  include_in_daily_summary: boolean
  sandbox_alerts: boolean
}

export interface SupportProject {
  id: number
  slug: string
  name: string
  persona_name: string | null
  brand_color: string | null
  mode: SupportMode
  sending_domain: string | null
  from_local: string
  inbound_domain: string | null
  hosted_domain: string | null
  app_url: string | null
  allowed_origins: string[] | null
  test_recipients: string[] | null
  context_url: string | null
  webhook_url: string | null
  feedback_board_id: number | null
  server_key_prefix: string | null
  /** The project's own Resend key is set (write-only: the API never returns it). */
  has_resend_key?: boolean
  settings: SupportProjectSettings
  /** `"Hannah at Casewell" <support@mail.trycasewell.com>`, null without a sending domain. */
  from: string | null
  live_at: string | null
  waiting_on_team: number
  unread: number
}

export type SupportProjectInput = Partial<
  Pick<
    SupportProject,
    | "name" | "persona_name" | "brand_color" | "sending_domain" | "from_local" | "inbound_domain" | "hosted_domain"
    | "app_url" | "allowed_origins" | "test_recipients" | "context_url" | "webhook_url" | "feedback_board_id"
  >
> & { settings?: Partial<SupportProjectSettings> }

export interface SupportProjectCreated {
  data: SupportProject
  identity_secret: string
  server_key: string
}

export type SupportSecret = "identity_secret" | "server_key"

export interface SupportContact {
  /** The product's own user id; null for an anonymous (contact form, email) person. */
  id: string | null
  email: string | null
  name: string | null
  verified: boolean
}

export interface SupportConversationRow {
  /** Public id, cv_… */
  id: string
  number: number
  /** Project slug. */
  project: string | null
  project_name: string | null
  brand_color: string | null
  kind: SupportKind
  status: SupportStatus
  subject: string | null
  category: SupportCategory | null
  external_key: string | null
  org: { id: string | null; name: string | null } | null
  contact: SupportContact | null
  channel: string | null
  created_at: string | null
  updated_at: string | null
  last_message_at: string | null
  test: boolean
  spam: boolean
  alert: boolean
  unread: boolean
  assignee_id: number | null
  task_id: number | null
  satisfaction: string | number | null
  first_response_at: string | null
  url: string
  preview: { type: SupportMessageType; text: string; author: string | null } | null
}

export interface SupportAttachment {
  id: string
  name: string
  type: string
  size: number
}

export interface SupportMessage {
  id: string
  type: SupportMessageType
  body: string
  author: { name: string | null; kind: "customer" | "team" | "system" }
  attachments: SupportAttachment[]
  created_at?: string
  /** Absent when the message never had an email state. */
  email_status?: SupportEmailStatus
  email_preview?: string | null
}

export interface SupportEvent {
  event: string
  data: Record<string, unknown> | null
  created_at: string
}

export interface SupportConversation extends SupportConversationRow {
  ctx: Record<string, unknown> | null
  source_url: string | null
  messages: SupportMessage[]
  events: SupportEvent[]
  context_available: boolean
}

export interface SupportConversationList {
  data: SupportConversationRow[]
  current_page: number
  last_page: number
  total: number
}

export interface SupportCounts {
  unread: number
  waiting_on_team: number
  spam: number
}

export interface SupportContextRow {
  label: string
  value?: string | null
  url?: string | null
  tone?: ContextTone
}

export interface SupportContext {
  available: boolean
  error?: string
  title?: string | null
  subtitle?: string | null
  sections?: { title: string; rows: SupportContextRow[] }[]
  links?: { label: string; url: string }[]
}

export interface SupportBlock {
  id: number
  project_id: number | null
  type: SupportBlockType
  value: string
  reason: string | null
  created_at: string | null
}

export interface SupportListFilters {
  folder: SupportFolder
  project: string
  kind: string
  status: string
  q: string
  page: number
}

/* ------------------------------------------------------------------------ */
/* Labels                                                                    */
/* ------------------------------------------------------------------------ */

export const FOLDERS: { value: SupportFolder; label: string }[] = [
  { value: "inbox", label: "Inbox" },
  { value: "waiting", label: "Waiting on us" },
  { value: "all", label: "All" },
  { value: "closed", label: "Closed" },
  { value: "spam", label: "Spam" },
  { value: "test", label: "Test" },
]

export const STATUS_LABEL: Record<SupportStatus, string> = {
  open: "Open",
  waiting_on_team: "Waiting on us",
  waiting_on_customer: "Waiting on customer",
  resolved: "Resolved",
  closed: "Closed",
}

export const STATUSES = Object.keys(STATUS_LABEL) as SupportStatus[]

export const KIND_LABEL: Record<SupportKind, string> = {
  contact: "Contact",
  support: "Support",
  feedback: "Feedback",
}

export const KINDS = Object.keys(KIND_LABEL) as SupportKind[]

export const CATEGORY_LABEL: Record<SupportCategory, string> = {
  bug: "Bug",
  idea: "Idea",
  question: "Question",
  other: "Other",
}

type Tone = "success" | "info" | "warning" | "error" | "violet" | "neutral"

export function statusTone(status: SupportStatus): Tone {
  switch (status) {
    case "waiting_on_team":
      return "warning"
    case "open":
      return "info"
    case "waiting_on_customer":
      return "violet"
    case "resolved":
      return "success"
    default:
      return "neutral"
  }
}

export const EMAIL_STATUS: Record<SupportEmailStatus, { label: string; tone: Tone; title: string }> = {
  sent: { label: "Emailed", tone: "success", title: "Sent to the customer" },
  held: { label: "Held (sandbox)", tone: "warning", title: "The project is in sandbox, so the customer was not emailed" },
  test: { label: "Test, not emailed", tone: "violet", title: "A test conversation never emails the customer" },
  failed: { label: "Email failed", tone: "error", title: "Sending failed" },
  none: { label: "No email", tone: "neutral", title: "Nothing was emailed for this message" },
}

export const CONTEXT_TONE_CLASS: Record<ContextTone, string> = {
  default: "text-foreground",
  success: "text-[#00713fde] dark:text-[#46fea5d4]",
  warning: "text-[#ab6400] dark:text-[#ffca16]",
  danger: "text-[#c40006d3] dark:text-[#ff9592]",
}

/** The conversation's kind and category: "Feedback · Bug", or just "Contact". */
export function kindLabel(c: Pick<SupportConversationRow, "kind" | "category">): string {
  const kind = KIND_LABEL[c.kind] ?? c.kind
  return c.category ? `${kind} · ${CATEGORY_LABEL[c.category] ?? c.category}` : kind
}

/** Who wrote in: name, else email, else "Anonymous". */
export function contactLabel(contact: SupportContact | null): string {
  if (!contact) return "Anonymous"
  return contact.name || contact.email || "Anonymous"
}

/**
 * A contact the product did not vouch for: anonymous people (contact form,
 * email) are never verified, so anything they claim about themselves is
 * unproven.
 */
export function isUnverified(contact: SupportContact | null): boolean {
  return !!contact && !contact.verified
}

/** The project's brand color, or a neutral gray when it has none (or a malformed one). */
export function brandColor(color: string | null | undefined): string {
  return color && /^#[0-9a-f]{6}$/i.test(color) ? color : "#8b8d98"
}

export function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

/** Event names from the ledger, as a sentence: "secret.rotated" → "Secret rotated". */
export function eventLabel(event: string): string {
  const known: Record<string, string> = {
    "conversation.created": "Conversation opened",
    "message.created": "Message added",
    "status.changed": "Status changed",
    "marked.spam": "Marked spam",
    "marked.not_spam": "Marked not spam",
    "task.created": "Task created",
    "secret.rotated": "Secret rotated",
    "mode.changed": "Mode changed",
  }
  if (known[event]) return known[event]
  const words = event.replace(/[._]+/g, " ").trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** A short "key: value" summary of an event's data, without ids that mean nothing to a reader. */
export function eventDetail(data: Record<string, unknown> | null): string {
  if (!data) return ""
  return Object.entries(data)
    .filter(([k, v]) => v !== null && v !== undefined && v !== "" && k !== "by" && !k.endsWith("_id"))
    .map(([k, v]) => `${k.replace(/_/g, " ")} ${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
    .join(", ")
}

/** Filters from the query string; anything unknown falls back to the inbox defaults. */
export function filtersFromParams(params: URLSearchParams): SupportListFilters {
  const folder = params.get("folder")
  const kind = params.get("kind") ?? ""
  const status = params.get("status") ?? ""
  const page = Number(params.get("page") ?? "1")
  return {
    folder: FOLDERS.some((f) => f.value === folder) ? (folder as SupportFolder) : "inbox",
    project: params.get("project") ?? "",
    kind: (KINDS as string[]).includes(kind) ? kind : "",
    status: (STATUSES as string[]).includes(status) ? status : "",
    q: params.get("q") ?? "",
    page: Number.isInteger(page) && page > 0 ? page : 1,
  }
}

/** The API query for a list: empty filters and the default folder are left out. */
export function listQuery(f: SupportListFilters): Record<string, string | number> {
  const out: Record<string, string | number> = { folder: f.folder }
  if (f.project) out.project = f.project
  if (f.kind) out.kind = f.kind
  if (f.status) out.status = f.status
  if (f.q.trim()) out.q = f.q.trim()
  if (f.page > 1) out.page = f.page
  return out
}

/** Test recipients must be agency addresses (the API refuses anything else). */
export function isAgencyAddress(email: string): boolean {
  return /^[^@\s]+@(gluska\.co|snowballrank\.com)$/i.test(email.trim())
}

/** An allowed origin is a scheme and host only, no path: https://app.example.com */
export function isOrigin(value: string): boolean {
  return /^https?:\/\/[^/\s]+$/.test(value.trim())
}

export function isDomain(value: string): boolean {
  return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(value.trim())
}

/** One entry per line or comma, trimmed, without blanks or repeats. */
export function parseList(text: string): string[] {
  return [...new Set(text.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean))]
}
